// Copyright 2025, University of Maryland, All Rights Reserved
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUsersApi, type UsersApi } from '../services/users-api';
import { UserCog, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppConfigProvider } from '../context/AppConfigContext';
import { clearWorkspaceRouteParams } from '../lib/workspace-utils';
import { useUserProfile, USER_PROFILE_QUERY_KEY } from '../hooks/use-user-profile';

interface ImpersonationBannerProps {
  app_id?: string;
  debug?: boolean;
  /** Optional API instance for testing */
  usersApi?: UsersApi;
}

interface ImpersonationBannerInnerProps {
  usersApi?: UsersApi;
}

/**
 * Internal component that uses the hooks
 */
function ImpersonationBannerInner({ usersApi: injectedApi }: ImpersonationBannerInnerProps) {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const defaultApi = useUsersApi();
  const usersApi = injectedApi ?? defaultApi;

  // Fetch user profile to get impersonation status using standardized hook
  const { data: userProfile } = useUserProfile(isAuthenticated);

  // Set impersonation mutation
  const impersonateMutation = useMutation({
    mutationFn: (targetUserId: string | null) => usersApi.setUserImpersonation(targetUserId),
    onSuccess: () => {
      // cm-coding: The hash still names the impersonated user's workspace. Remove that
      // cross-user route state so authentication restores the administrator's
      // own saved workspace after reload.
      clearWorkspaceRouteParams();
      queryClient.invalidateQueries({ queryKey: USER_PROFILE_QUERY_KEY });
      // Force a full page reload to reset the auth context with new impersonation
      window.location.reload();
    },
  });

  const isImpersonating = userProfile?.impersonatedBy != null;

  const handleStopImpersonation = () => {
    impersonateMutation.mutate(null);
  };

  // Don't render if not impersonating
  if (!isImpersonating) {
    return null;
  }

  return (
    <div data-testid="admin-impersonation-banner" className="px-3 py-2 bg-amber-100 border border-amber-300 rounded-md flex items-center justify-between">
      <div className="flex items-center gap-2">
        <UserCog className="h-4 w-4 text-amber-700" />
        <span className="text-sm font-medium text-amber-900">
          Viewing as: {userProfile?.userId} (by admin: {userProfile?.impersonatedBy?.userId})
        </span>
      </div>
      <button
        onClick={handleStopImpersonation}
        disabled={impersonateMutation.isPending}
        className="px-2 py-1 text-xs bg-amber-200 hover:bg-amber-300 text-amber-900 rounded flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Stop impersonation"
        data-testid="admin-stop-impersonate"
      >
        <X className="h-3 w-3" />
        {impersonateMutation.isPending ? 'Stopping...' : 'Stop'}
      </button>
    </div>
  );
}

/**
 * ImpersonationBanner Component
 *
 * A reusable banner that displays when an admin is impersonating another user.
 * Shows who is being impersonated and provides a button to stop impersonation.
 *
 * Note: This component now relies on a parent QueryClientProvider.
 * The parent app must wrap this component with QueryClientProvider.
 *
 * @example
 * ```tsx
 * import { ImpersonationBanner } from '@umd-dit-ai-workspace/workspaces-component';
 *
 * function Layout() {
 *   return (
 *     <div>
 *       <ImpersonationBanner app_id="docreview" />
 *       <YourContent />
 *     </div>
 *   );
 * }
 * ```
 */
export function ImpersonationBanner({ app_id = 'default', debug = false, usersApi }: ImpersonationBannerProps) {
  return (
    <AppConfigProvider app_id={app_id} debug={debug}>
      <ImpersonationBannerInner usersApi={usersApi} />
    </AppConfigProvider>
  );
}
